import { useEffect, useState } from "react";
import {
	DialogContent,
	FormControlLabel,
	Checkbox,
	TextField,
	Select,
	MenuItem,
} from "@mui/material";
import {
	articleTypeOptions,
	articleClusterOptions,
} from "@/dentlab/src/lib/constants/options";
import { ArticleTypeEnum } from "@/lib/supabase/supabaseEnums";
import { Member } from "@/dentlab/src/store/UserManagement/user-management.store";
import { supabase } from "@/lib/supabase/supabaseClient";
import { JobSelectionComponent } from "./job-selection-pending-docs.component";
import {
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { DocumentSelectionComponent } from "./document-selection.component";
import { useCentralStore } from "../../store/Central";
import { showNotification } from "../../store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { CustomMobileDialog } from "./custom-mobile-dialog.component";
import { useNavigate } from "react-router-dom";

export const AttachMaterialDialog = ({
	open,
	lotNumber,
	onClose,
	jobId,
	jobDocumentId,
}: {
	open: boolean;
	lotNumber: string;
	onClose: (success: boolean) => void;
	jobId?: string;
	jobDocumentId?: string;
}) => {
	const navigate = useNavigate();
	const { activeUsers } = useCentralStore.getState();

	const [formData, setFormData] = useState({
		code_e: "MAT",
		amount: "1",
		technician: "",
		description: "",
		note: "",
		lot: lotNumber || "",
		isZAZ: false,
		discount: "",
		pricePerUnit: "",
		hasWarranty: false,
		articleType: ArticleTypeEnum.MATERIAL,
		cluster: "",
		manufacturer: "",
		ce_number: "",
		composition: "",
		job_id: "",
		job_document_id: "",
		client_id: "",
	});

	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			lot: lotNumber || "",
		}));
	}, [lotNumber]);

	const articleOptions = articleTypeOptions.filter(
		(option) =>
			option.value === ArticleTypeEnum.MATERIAL ||
			option.value === ArticleTypeEnum.EXTERNAL_WORK
	);

	const [dialogProgress, setDialogProgress] = useState(0);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const upsertItems = async () => {
			try {
				const { error } = await supabase
					.from(SupabaseTableEnum.JOB_ITEMS)
					.insert({
						code_e: formData.code_e,
						quantity: Number(formData.amount) ?? 1,
						description: formData.description,
						note: formData.note,
						discount: Number(formData.discount) ?? 0,
						price: Number(formData.pricePerUnit) ?? 0,
						job_document_id: Number(formData.job_document_id),
						is_warranty: formData.hasWarranty,
						is_req_dr_tax: formData.isZAZ,
						tp_value: 0,
						lot: formData.lot,
						profile_id: formData.technician || null,
						type: Number(formData.articleType),
						cluster: formData.cluster,
						code: formData.code_e,
					});

				if (error) {
					Logger.log("Fehler beim Speichern:", error);
					showNotification({
						message: "Fehler beim Speichern des Materials",
						type: "error",
					});
					onClose(false);
					return;
				}

				onClose(true);
				navigate(`/job/${formData.job_id}`);
			} catch (error) {
				Logger.log("Fehler beim Verarbeiten der Daten:", error);
				showNotification({
					message: "Fehler beim Speichern des Materials",
					type: "error",
				});
				onClose(false);
			}
		};

		if (formData.job_document_id && formData.job_id) {
			upsertItems();
		}
	}, [formData]);

	const handleChange = (field: string, value: any) => {
		setFormData((prev) => ({
			...prev,
			[field]: typeof value === 'boolean' ? value : String(value),
		}));
	};

	const handleBack = () => {
		if (dialogProgress > 0) {
			setDialogProgress(dialogProgress - 1);
		} else {
			onClose(false);
		}
	};

	const handleNext = () => {
		if (jobId && jobDocumentId) {
			setFormData(prev => ({
				...prev,
				job_id: jobId,
				job_document_id: jobDocumentId
			}));
		} else if (dialogProgress < 2) {
			setDialogProgress(dialogProgress + 1);
		}
	};

	const Dialogs = [
		{
			title: "Angaben zum Material",
			content: (
				<>
					<div className="flex flex-row gap-2">
						<TextField
							fullWidth
							label="Code*"
							value={formData.code_e}
							onChange={(e) =>
								handleChange("code_e", e.target.value)
							}
							size="small"
						/>

						<TextField
							fullWidth
							label="Menge*"
							type="number"
							value={formData.amount}
							onChange={(e) =>
								handleChange("amount", e.target.value)
							}
							size="small"
						/>
					</div>
					<div className="flex flex-row gap-2">
						<TextField
							fullWidth
							label="LOT*"
							value={formData.lot}
							onChange={(e) =>
								handleChange("lot", e.target.value)
							}
							size="small"
						/>
						<TextField
							fullWidth
							label="Preis/Einheit*"
							type="number"
							value={formData.pricePerUnit}
							onChange={(e) =>
								handleChange("pricePerUnit", e.target.value)
							}
							size="small"
						/>
					</div>
					{/* Selects */}
					<Select
						fullWidth
						value={formData.cluster}
						onChange={(e) =>
							handleChange("cluster", String(e.target.value))
						}
						size="small"
						displayEmpty
					>
						<MenuItem value="">Cluster wählen*</MenuItem>
						{articleClusterOptions.map(
							(option: { value: string; label: string }) => (
								<MenuItem
									key={option.value}
									value={option.value}
								>
									{option.label}
								</MenuItem>
							)
						)}
					</Select>
					<Select
						fullWidth
						value={formData.articleType}
						onChange={(e) =>
							handleChange("articleType", String(e.target.value))
						}
						size="small"
					>
						{articleOptions.map(
							(option: {
								value: ArticleTypeEnum;
								label: string;
							}) => (
								<MenuItem
									key={option.value}
									value={option.value}
								>
									{option.label}
								</MenuItem>
							)
						)}
					</Select>
						<Select
							fullWidth
							value={formData.technician}
							onChange={(e) =>
								handleChange("technician", e.target.value)
							}
							size="small"
							displayEmpty
						>
							<MenuItem value="">Kein Techniker</MenuItem>
							{activeUsers.map((user: Member) => (
								<MenuItem key={user.id} value={user.id}>
									{user.first_name && user.last_name
										? `${user.first_name} ${user.last_name}`
										: user.email}
								</MenuItem>
							))}
						</Select>
					<TextField
						fullWidth
						label="Bezeichnung"
						value={formData.description}
						onChange={(e) =>
							handleChange("description", e.target.value)
						}
						size="small"
					/>
					<TextField
						fullWidth
						label="Hersteller"
						value={formData.manufacturer}
						onChange={(e) =>
							handleChange("manufacturer", e.target.value)
						}
						size="small"
					/>
					<TextField
						fullWidth
						label="Zusammensetzung"
						value={formData.composition}
						onChange={(e) =>
							handleChange("composition", e.target.value)
						}
						size="small"
					/>
					

					{/* Weitere Details */}
					<TextField
						fullWidth
						label="Notiz"
						multiline
						rows={2}
						value={formData.note}
						onChange={(e) => handleChange("note", e.target.value)}
						size="small"
					/>

					{/* Numerische Felder */}
					<div className="flex flex-row gap-2">
						<TextField
							fullWidth
							label="Rabatt %"
							type="number"
							value={formData.discount}
							onChange={(e) =>
								handleChange("discount", e.target.value)
							}
							size="small"
						/>
						<TextField
							fullWidth
							label="CE-Nummer"
							value={formData.ce_number}
							onChange={(e) =>
								handleChange("ce_number", e.target.value)
							}
							size="small"
						/>
					</div>
					
					{/* Checkboxen */}
					<div className="flex flex-row justify-between">
						<FormControlLabel
							control={
								<Checkbox
									checked={formData.isZAZ}
									onChange={(e) =>
										handleChange("isZAZ", e.target.checked)
									}
								/>
							}
							label="ZAZ-St."
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={formData.hasWarranty}
									onChange={(e) =>
										handleChange("hasWarranty", e.target.checked)
									}
								/>
							}
							label="Garantie"
						/>
					</div>
					<div className="flex flex-row justify-between gap-2">
						<button
							className="bg-[#003261] text-white px-4 py-2 rounded-md w-1/2"
							onClick={handleBack}
						>
							Zurück
						</button>
						<button
							className="bg-[#003261] disabled:bg-[#003261]/40 text-white px-4 py-2 rounded-md w-1/2"
							onClick={handleNext}
							disabled={
								!formData.code_e ||
								!formData.amount ||
								!formData.lot ||
								!formData.pricePerUnit ||
								!formData.cluster ||
								!formData.articleType
							}
						>
							Weiter
						</button>
					</div>
				</>
			),
		},
		{
			title: "Auftrag auswählen",
			content: (
				<div className="flex flex-col h-[80vh]">
					<div className="flex flex-col flex-grow overflow-y-auto h-[calc(100%-60px)]">
						<JobSelectionComponent
							handleSelect={(job) => {
								handleChange("job_id", String(job.id));
								handleChange("client_id", String(job.client_id));
								handleNext();
							}}
							setLoading={setLoading}
							loading={loading}
						/>
					</div>
					<div className="flex-1 bg-white">
						<button
							className="bg-[#003261] text-white px-4 py-2 rounded-md w-full"
							onClick={handleBack}
						>
							Zurück
						</button>
					</div>
				</div>
			),
		},
		{
			title: "Dokument auswählen",
			content: (
				<div className="h-full flex flex-col gap-6 overflow-y-auto justify-between items-between">
					<DocumentSelectionComponent
						handleSelect={(jobDocument) => {
							handleChange("job_document_id", String(jobDocument.id));
						}}
						setLoading={setLoading}
						loading={loading}
						jobId={formData.job_id}
						clientId={formData.client_id}
					/>
						<button
							className="bg-[#003261] text-white px-4 py-2 rounded-md w-full"
							onClick={handleBack}
						>
							Zurück
						</button>
				</div>
			),
		},
	];

	return (
		<CustomMobileDialog
			open={open}
			onClose={() => onClose(false)}
			title={Dialogs[dialogProgress].title}
		>
			<DialogContent>
				<div className="flex flex-col gap-2 overflow-y-auto pt-2">
					{Dialogs[dialogProgress].content}
				</div>
			</DialogContent>
		</CustomMobileDialog>
	);
};
